import { use, type ReactElement } from 'react';
import { useRouteLoaderData } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import {
    emptyCategoryProductCounts,
    getCategoryProductCountById,
    type CategoryProductCounts,
} from '@/lib/category-product-counts';
import { resolveLinkTargets } from '@/lib/seo-linking/resolve-links';
import SeoLinkingSection from '@/components/seo-linking/seo-linking-section';
import { parseCgidFromRefine } from '@/lib/refine-cgid';

type CategoryCountsLoaderData = {
    categoryProductCounts?: Promise<CategoryProductCounts | undefined>;
};

export type SeoLinkingWrapperProps = {
    current: ShopperProducts.schemas['Category'] | null | undefined;
    parentForSiblings?: ShopperProducts.schemas['Category'] | null;
    refine?: string[];
    className?: string;
    navAriaLabel?: string;
};

/**
 * Resolves link targets from category taxonomy; renders nothing when no links apply.
 */
export default function SeoLinkingWrapper({
    current,
    parentForSiblings = null,
    className,
    navAriaLabel,
    refine,
}: SeoLinkingWrapperProps): ReactElement | null {
    const { t } = useTranslation('seoLinking');
    const appData = useRouteLoaderData<CategoryCountsLoaderData>('routes/_app');
    const counts = use(appData?.categoryProductCounts ?? emptyCategoryProductCounts);
    const productCountById = getCategoryProductCountById(counts);

    if (!current) {
        return null;
    }
    const refineId = parseCgidFromRefine(refine) ?? undefined;
    const links = resolveLinkTargets({ current, parentForSiblings, refineId, productCountById });
    if (!links) {
        return null;
    }

    return (
        <SeoLinkingSection className={className} title={t('sectionTitle')} navAriaLabel={navAriaLabel} links={links} />
    );
}
